import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  styled,
} from "@mui/material";
import { useGetWorkdataQuery } from "../../../services/roleReviewService";
import useAuth from "../../../hooks/useAuth";
import { useLocation } from "react-router-dom";

const WorkData = ({ userId }) => {
  const { users } = useAuth();
  const location = useLocation();
  const isJwtUser = location.state?.isJwtUser || false;
  const userIdFromState = location.state?.userId;
  let employeeId;

  if (isJwtUser) {
    employeeId = userIdFromState;
  } else if (users?.role === "manager" || users?.role === "superadmin") {
    employeeId = userId; // Use the userId prop for managers and superadmins
  } else {
    employeeId = users?.user_id;
  }

  const {
    data,
    error,
    isLoading,
    refetch: workdata_refetch,
  } = useGetWorkdataQuery(employeeId);

  useEffect(() => {
    if (data) {
      workdata_refetch();
      console.log(data.data);
    }
  }, [data, workdata_refetch]);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error)
    return <Typography color="error">Error: {error.message}</Typography>;

  if (data?.data?.detail?.includes("No work data found")) {
    return (
      <>
        <Box className="pdf-avoid-break" sx={{ marginBottom: 0 }}>
          <CustomHead>Areas of Concern</CustomHead>
          <CustomText>
            Here are the items, time commitment, and notes that come to mind
            when prompted with the questions:
          </CustomText>
          <CustomText>
            - What work are you doing that you know or suspect is outside the
            scope of your role?
          </CustomText>
          <CustomText sx={{ marginBottom: "30px" }}>
            - How many hours per month does this pull you away from the work
            you're being expected to perform?
          </CustomText>
          <TableContainer sx={{ maxHeight: "auto", marginBottom: "15px" }}>
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <ReportCustomTableHead>
                <TableRow>
                  <ReportCustomTableCell>
                    Item Outside Scope of Role
                  </ReportCustomTableCell>
                  <ReportCustomTableCell>
                    Estimated Hours/Mo
                  </ReportCustomTableCell>
                </TableRow>
              </ReportCustomTableHead>
              <ReportCustomTableBody>
                <TableRow>
                  <ReportCustomTableCellData colSpan={2} align="center">
                    Total Estimated Hours
                  </ReportCustomTableCellData>
                </TableRow>
              </ReportCustomTableBody>
            </Table>
          </TableContainer>
          <CommentsSection className="pdf-avoid-break">
            <CustomText sx={{ marginBottom: "8px" }}>
              Relevant comments/context/thoughts:
            </CustomText>
            <CommentsBox>No comments available</CommentsBox>
          </CommentsSection>
        </Box>
      </>
    );
  }

  const totalHours =
    data?.data?.work_data.reduce(
      (sum, item) => sum + item.hours_per_month,
      0
    ) || 0;

  return (
    <>
      <Box className="pdf-avoid-break" sx={{ marginBottom: 0 }}>
        <CustomHead>Areas of Concern</CustomHead>
        <CustomText>
          Here are the items, time commitment, and notes that come to mind when
          prompted with the questions:
        </CustomText>
        <CustomText>
          - What work are you doing that you know or suspect is outside the
          scope of your role?
        </CustomText>
        <CustomText sx={{ marginBottom: "30px" }}>
          - How many hours per month does this pull you away from the work
          you're being expected to perform?
        </CustomText>
        <TableContainer sx={{ maxHeight: "auto", marginBottom: "15px" }}>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <ReportCustomTableHead>
              <TableRow>
                <ReportCustomTableCell>
                  Item Outside Scope of Role
                </ReportCustomTableCell>
                <ReportCustomTableCell>
                  Estimated Hours/Mo
                </ReportCustomTableCell>
              </TableRow>
            </ReportCustomTableHead>
            <ReportCustomTableBody>
              {data?.data?.work_data.map((item, index) => (
                <TableRow key={index} sx={{ background:"#fae1d4" }}>
                  <ReportCustomTableCellDataTwo>
                    {item.work_description}
                  </ReportCustomTableCellDataTwo>
                  <ReportCustomTableCellDataTwo>
                    {item.hours_per_month}
                  </ReportCustomTableCellDataTwo>
                </TableRow>
              ))}
              <TableRow>
                <ReportCustomTableCellData>
                  Total Estimated Hours
                </ReportCustomTableCellData>
                <ReportCustomTableCellData>
                  {totalHours}
                </ReportCustomTableCellData>
              </TableRow>
            </ReportCustomTableBody>
          </Table>
        </TableContainer>
        <CommentsSection className="pdf-avoid-break">
          <CustomText sx={{ marginBottom: "8px" }}>
            Relevant comments/context/thoughts:
          </CustomText>
          <CommentsBox>
            {data?.data?.comments || "No comments available"}
          </CommentsBox>
        </CommentsSection>
      </Box>
    </>
  );
};

export default WorkData;

const ReportCustomTableHead = styled(TableHead)(() => ({
  background: "#1085AB",
}));
const tableCellWrapStyles = {
  wordWrap: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "normal",
};

const ReportCustomTableCell = styled(TableCell)(() => ({
  fontSize: "16px",
  fontWeight: "600",
  lineHeight: "1.2",
  color: "#ffffff",
  padding: "15px 15px",
  border: "0",
  ...tableCellWrapStyles,
}));
const ReportCustomTableCellData = styled(TableCell)(() => ({
  fontSize: "16px",
  fontWeight: "700",
  lineHeight: "1.2",
  color: "#333333",
  padding: "10px 15px",
  border: "0",
  ...tableCellWrapStyles,
}));
const ReportCustomTableCellDataTwo = styled(TableCell)(() => ({
  fontSize: "16px",
  fontWeight: "400",
  lineHeight: "1.2",
  color: "#333333",
  padding: "10px 15px",
  border: "0",
  ...tableCellWrapStyles,
}));
const ReportCustomTableBody = styled(TableBody)(() => ({
  border: "0",
  background: "#fae1d4",
}));
const CustomHead = styled(Typography)(() => ({
  fontSize: "22px",
  fontWeight: "600",
  lineHeight: "1.2",
  color: "#333333",
  marginBottom: "5px",
  "@media (max-width: 575px)": {
    fontSize: "22px",
  },
}));
const CustomText = styled(Typography)(() => ({
  fontSize: "16px",
  fontWeight: "400",
  lineHeight: "1.5",
  color: "#333333",
  marginBottom: "5px",
  "@media (max-width: 575px)": {
    fontSize: "16px",
  },
}));
const CommentsSection = styled(Box)(() => ({
  width: "100%",
  marginBottom: 0,
}));

const CommentsBox = styled(Box)(() => ({
  border: "1px solid #DADADA",
  background: "#fae1d4",
  borderRadius: "7px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: "300",
  lineHeight: "1.5",
  color: "#2C2C2E",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  width: "100%",
  boxSizing: "border-box",
  minHeight: "48px",
}));
